import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axios, useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import yedkursLogo from '../../images/yedkursLogo.png';
import {
  DEFAULT_CONTRACT_DOCUMENT_INFO,
  getContractStudentName as getStudentName,
  normalizeContractDocumentInfo,
  printCourseContractDocument,
  printPromissoryNotesDocument,
  printPaymentReceiptDocument,
  makeReceiptNo,
  paymentMethodLabel,
} from '../../services/contractDocuments';
import {
  buildSelectedInstallmentPaymentPreview,
  reconcileInstallmentPayments,
  summarizePaymentsByContract,
} from '../../services/installmentAccounting';

const TL = (v) => new Intl.NumberFormat('tr-TR',{style:'currency',currency:'TRY',minimumFractionDigits:0}).format(v||0);
const taksitKalani = taksit => Math.max(0, Number(
  taksit?.kalan_tutar ?? (Number(taksit?.tutar || 0) - Number(taksit?.odeme_tutari || 0))
));

const tahsilatOnizleme = (taksitler, secili, tutar) => {
  const preview = buildSelectedInstallmentPaymentPreview(
    taksitler,
    tutar,
    secili?.sozlesme_id,
    secili?.id
  );
  return {
    dagitimlar: preview.allocations.map(item => ({
      ...item,
      uygulanan: item.applied,
      yeniKalan: item.after,
    })),
    fazla: preview.extra,
    toplamKalan: preview.totalRemaining,
  };
};

const PAKETLER = ['TYT Hazırlık','AYT Sayısal','AYT Sözel','AYT Eşit Ağırlık','LGS','KPSS','YDS','Tüm Paket'];
const MAX_TAKSIT_SAYISI = 20;
const SOZLESME_PAGE_SIZE = 15;

const normalizeClassName = value => String(value || '')
  .trim()
  .replace(/\s+/g, ' ')
  .toLocaleLowerCase('tr-TR');

const contractMatchesClass = (contract, selectedClass) => {
  if (!selectedClass) return true;

  if (contract?.sinif_id !== undefined && contract?.sinif_id !== null && contract?.sinif_id !== '') {
    return String(contract.sinif_id) === String(selectedClass.id);
  }

  const selectedClassName = selectedClass.ad || selectedClass.sinif_adi || selectedClass.name;
  return normalizeClassName(contract?.sinif_adi) === normalizeClassName(selectedClassName);
};

const localToday = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};

const addMonths = (date, months) => {
  const [year, month, day] = String(date || localToday()).slice(0, 10).split('-').map(Number);
  const targetMonthIndex = month - 1 + months;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const targetMonth = ((targetMonthIndex % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  return [
    String(targetYear).padStart(4, '0'),
    String(targetMonth + 1).padStart(2, '0'),
    String(Math.min(day, lastDay)).padStart(2, '0'),
  ].join('-');
};

const asBool = (value) => value === true || value === 1 || value === '1' || value === 'true';

const extractList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const printCourseContract = ({ form, student }) => {
  const opened = printCourseContractDocument({ form, student, logoUrl: yedkursLogo });
  if (!opened) toast.error('Sözleşme penceresi açılamadı. Tarayıcı popup iznini kontrol edin.');
};

const printPromissoryNotes = ({ form, student }) => {
  const opened = printPromissoryNotesDocument({ form, student, logoUrl: yedkursLogo });
  if (!opened) toast.error('Senet penceresi açılamadı. Tarayıcı popup iznini kontrol edin.');
};

// ── Modal: Yeni Sözleşme ───────────────────────────────────────── 
const initialManualInstallment = (date) => ({
  vade_tarihi: date,
  tutar: '',
  durum: 'bekliyor',
  odeme_tarihi: '',
  odeme_yontemi: '',
});

const buildStudentFormInfo = (student = {}) => ({
  ad: student.ad || '',
  soyad: student.soyad || '',
  ad_soyad: student.ad_soyad || [student.ad, student.soyad].filter(Boolean).join(' '),
  okul_adi: student.okul_adi || student.okul || '',
  sinif_adi: student.sinif_adi || student.sinif || '',
  sinav_turu: student.sinav_turu || student.egitim_turu || '',
  alan: student.alan || '',
  anne_adi: student.anne_adi || '',
  anne_telefon: student.anne_telefon || '',
  baba_adi: student.baba_adi || '',
  baba_telefon: student.baba_telefon || '',
  telefon: student.telefon || student.ogrenci_telefon || '',
  tc_kimlik: student.tc_kimlik || student.tc_no || '',
  dogum_tarihi: student.dogum_tarihi || student.ogrenci_dogum_tarihi ? String(student.dogum_tarihi || student.ogrenci_dogum_tarihi).slice(0, 10) : '',
  adres: student.adres || '',
  veli_adi: student.veli_adi || student.veli_ad_soyad || '',
  veli_telefon: student.veli_telefon || student.veli_tel || '',
  veli_email: student.veli_email || '',
  veli_meslek: student.veli_meslek || '',
  sehit_gazi_cocugu: asBool(student.sehit_gazi_cocugu),
  sosyal_medya_izni: asBool(student.sosyal_medya_izni),
});

function SozlesmeModal({ sablonlar, onSave, onClose }) {
  const today = localToday();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSinif, setSelectedSinif] = useState('');
  const [ogrenciler, setOgrenciler] = useState([]);
  const [siniflar, setSiniflar] = useState([]);
  const [ogrenciLoading, setOgrenciLoading] = useState(false);
  const [form, setForm] = useState({
    ogrenci_id:'', egitim_yili: new Date().getFullYear()+'-'+(new Date().getFullYear()+1),
    paket:'TYT Hazırlık', egitim_turu:'TYT',
    baslangic_tarihi: today,
    bitis_tarihi: addMonths(today, 9),
    brut_ucret:'', sablon_id:'', indirim_tipi:'', indirim_tutar:'0', indirim_yuzde:'0',
    net_ucret:'', odeme_tipi:'taksit', taksit_sayisi:'9',
    ilk_odeme_tarihi: today, taksit_modu:'otomatik',
    taksitler: [initialManualInstallment(today)],
    sozlesme_form_bilgileri: buildStudentFormInfo(),
    belge_bilgileri: { ...DEFAULT_CONTRACT_DOCUMENT_INFO },
    notlar:'',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setOgrenciLoading(true);
      try {
        const params = new URLSearchParams({ limit: '500' });
        if (searchTerm.trim()) params.set('arama', searchTerm.trim());
        if (selectedSinif.startsWith('id:')) params.set('sinif_id', selectedSinif.slice(3));
        if (selectedSinif.startsWith('ad:')) params.set('sinif', selectedSinif.slice(3));
        const r = await axios.get(`/muhasebe/sozlesmeler/ogrenci-secimi?${params}`);
        setOgrenciler(extractList(r.data));
      } catch {
        setOgrenciler([]);
      } finally {
        setOgrenciLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm, selectedSinif]);

  useEffect(() => {
    axios.get('/siniflar')
      .then(r => setSiniflar(extractList(r.data)))
      .catch(() => setSiniflar([]));
  }, []);

  // --- Yardımcı Fonksiyonlar ---
  const hesaplaNet = (brut, indirimT, indirimY) => {
    const b  = parseFloat(brut) || 0;
    const it = parseFloat(indirimT) || 0;
    const iy = parseFloat(indirimY) || 0;
    const ind = it > 0 ? it : (b * iy / 100);
    return Math.max(0, b - ind);
  };

  const handleChange = (field, val) => {
    setForm(f => {
      const nf = { ...f, [field]: val };
      if (['brut_ucret','indirim_tutar','indirim_yuzde'].includes(field)) {
        nf.net_ucret = hesaplaNet(nf.brut_ucret, nf.indirim_tutar, nf.indirim_yuzde).toFixed(2);
      }
      return nf;
    });
  };

  const handleStudentSelect = (studentId) => {
    const student = ogrenciler.find(o => String(o.id) === String(studentId));
    setForm(f => ({
      ...f,
      ogrenci_id: studentId,
      egitim_turu: student?.sinav_turu || student?.egitim_turu || f.egitim_turu,
      sozlesme_form_bilgileri: buildStudentFormInfo(student),
      belge_bilgileri: normalizeContractDocumentInfo(f.belge_bilgileri, buildStudentFormInfo(student), f),
    }));
  };

  const handleFormInfoChange = (field, val) => {
    setForm(f => ({
      ...f,
      sozlesme_form_bilgileri: {
        ...f.sozlesme_form_bilgileri,
        [field]: val,
      },
    }));
  };

  const handleDocumentInfoChange = (field, value) => {
    setForm(current => ({
      ...current,
      belge_bilgileri: { ...current.belge_bilgileri, [field]: value },
    }));
  };

  const handleInstallmentChange = (index, field, value) => {
    setForm(f => ({
      ...f,
      taksitler: f.taksitler.map((row, i) => i === index ? { ...row, [field]: value } : row),
    }));
  };

  const addManualInstallment = () => {
    if (form.taksitler.length >= MAX_TAKSIT_SAYISI) {
      toast.error(`Sözleşme ödeme planına en fazla ${MAX_TAKSIT_SAYISI} taksit eklenebilir`);
      return;
    }
    setForm(f => ({
      ...f,
      taksitler: [...f.taksitler, initialManualInstallment(addMonths(f.ilk_odeme_tarihi || f.baslangic_tarihi, f.taksitler.length))],
    }));
  };

  const removeManualInstallment = (index) => {
    setForm(f => ({
      ...f,
      taksitler: f.taksitler.length > 1 ? f.taksitler.filter((_, i) => i !== index) : f.taksitler,
    }));
  };

  const manualTotal = form.taksitler.reduce((sum, t) => sum + (parseFloat(t.tutar) || 0), 0);
  const manualDiff = manualTotal - (parseFloat(form.net_ucret) || 0);

  const buildPayload = () => {
    const payload = { ...form };
    if (form.taksit_modu === 'manuel') {
      payload.taksitler = form.taksitler.map((t, i) => ({
        taksit_no: i + 1,
        vade_tarihi: t.vade_tarihi,
        tutar: parseFloat(t.tutar) || 0,
        durum: t.durum || 'bekliyor',
        ...(t.odeme_tarihi ? { odeme_tarihi: t.odeme_tarihi } : {}),
        ...(t.odeme_yontemi ? { odeme_yontemi: t.odeme_yontemi } : {}),
      }));
    } else {
      delete payload.taksitler;
    }
    return payload;
  };

  const handleSablonChange = (sablonId) => {
    if (!sablonId) {
      setForm(f => ({ ...f, sablon_id:'', indirim_tipi:'', indirim_tutar:'0', indirim_yuzde:'0',
        net_ucret: (parseFloat(f.brut_ucret)||0).toFixed(2) }));
      return;
    }
    const sablon = sablonlar.find(s => String(s.id) === sablonId);
    if (!sablon) return;
    const brut = parseFloat(form.brut_ucret) || 0;
    const yuzde = parseFloat(sablon.yuzde) || 0;
    const sabit = parseFloat(sablon.sabit_tutar) || 0;
    const indirimTutar = yuzde > 0 ? 0 : sabit;
    const indirimYuzde = yuzde > 0 ? yuzde : 0;
    const net = Math.max(0, brut - (yuzde > 0 ? brut * yuzde / 100 : sabit));
    setForm(f => ({
      ...f,
      sablon_id:     sablonId,
      indirim_tipi:  sablon.tip,
      indirim_tutar: indirimTutar.toFixed(2),
      indirim_yuzde: indirimYuzde.toFixed(2),
      net_ucret:     net.toFixed(2),
    }));
  };

  const handleSubmit = async () => {
    if (!form.ogrenci_id || !form.brut_ucret || !form.net_ucret) {
      toast.error('Zorunlu alanları doldurun'); return;
    }
    if (!form.belge_bilgileri.kurum_unvani.trim()) {
      toast.error('Sözleşme için kurumun resmî unvanını girin'); return;
    }
    if (form.odeme_tipi === 'senet' && !form.belge_bilgileri.borclu_ad_soyad.trim()) {
      toast.error('Senetli ödemede borçlu/veli adı zorunludur'); return;
    }
    if (form.odeme_tipi === 'senet' && (!form.belge_bilgileri.duzenleme_yeri.trim() || !form.belge_bilgileri.odeme_yeri.trim())) {
      toast.error('Senetli ödemede düzenleme ve ödeme yerini girin'); return;
    }
    if (form.taksit_modu === 'otomatik' && (Number(form.taksit_sayisi) < 1 || Number(form.taksit_sayisi) > MAX_TAKSIT_SAYISI)) {
      toast.error(`Sözleşme ödeme planı en fazla ${MAX_TAKSIT_SAYISI} taksit olabilir`); return;
    }
    if (form.taksit_modu === 'manuel') {
      const invalidRow = form.taksitler.find(t => !t.vade_tarihi || !(parseFloat(t.tutar) > 0) || !t.durum);
      if (invalidRow) {
        toast.error('Manuel taksitlerde vade tarihi, tutar ve durum zorunludur'); return;
      }
      if (Math.abs(manualDiff) > 0.01) {
        toast.error(`Manuel taksit toplamı net ücretten ${TL(Math.abs(manualDiff))} ${manualDiff > 0 ? 'fazla' : 'eksik'}`); return;
      }
    }
    setSaving(true);
    try { await onSave(buildPayload()); } finally { setSaving(false); }
  };

  const secilenSablon = sablonlar.find(s => String(s.id) === form.sablon_id);
  const filtreliOgrenciler = ogrenciler;
  const formInfo = form.sozlesme_form_bilgileri;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 960 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">📋 Yeni Sözleşme</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="muh-grid-2">
          
          {/* Öğrenci Seçimi ve Filtreleme Bölümü */}
          <div className="form-group" style={{ gridColumn: '1/-1', background: 'var(--bg-elevated)', padding: '15px', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <label className="form-label" style={{ fontWeight: 800, color: 'var(--blue)', marginBottom: '10px', display: 'block' }}>
              Öğrenci Seçimi *
            </label>
            
            <div className="muh-filter-row" style={{ marginBottom: 10 }}>
              <div style={{ flex: 2 }}>
                <input 
                  className="form-input" 
                  placeholder="🔍 İsme göre öğrenci ara..." 
                  value={searchTerm} 
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <select
                  className="form-input"
                  value={selectedSinif} 
                  onChange={e => setSelectedSinif(e.target.value)}
                >
                  <option value="">Tüm Sınıflar</option>
                  {siniflar.map(s => {
                    const label = s.ad || s.sinif_adi || s.name || `Sınıf #${s.id}`;
                    const value = s.id ? `id:${s.id}` : `ad:${label}`;
                    return <option key={value} value={value}>{label}</option>;
                  })}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', fontWeight: 700 }}>
                Listeden öğrenci seçin
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--blue)', fontWeight: 800 }}>
                {ogrenciLoading ? 'Yükleniyor...' : `${filtreliOgrenciler.length} sonuç`}
              </div>
            </div>
            <select
              className="form-input"
              size={Math.min(7, Math.max(filtreliOgrenciler.length, 4))}
              value={form.ogrenci_id}
              onChange={e => handleStudentSelect(e.target.value)}
              style={{ height: 'auto', padding: '6px', background: '#fff', border: '1.5px solid var(--border)' }}
            >
              {filtreliOgrenciler.length === 0 ? (
                <option value="" disabled>{ogrenciLoading ? 'Öğrenciler yükleniyor...' : 'Sonuç bulunamadı'}</option>
              ) : (
                filtreliOgrenciler.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.ad} {o.soyad} | {o.sinif_adi || 'Sınıf Yok'} | No: {o.ogrenci_no || '-'}
                  </option>
                ))
              )}
            </select>
            {form.ogrenci_id && (
              <div style={{ marginTop: 8, fontSize: '0.82rem', color: '#10B981', fontWeight: 700 }}>
                ✅ Seçili: {getStudentName(formInfo)}
              </div>
            )}
          </div>

          {form.ogrenci_id && (
            <div className="form-group" style={{ gridColumn:'1/-1', background:'var(--bg-elevated)', padding:15, borderRadius:12, border:'1px solid var(--border)' }}>
              <label className="form-label" style={{ fontWeight:800, marginBottom:10, display:'block' }}>
                Sözleşme Form Bilgileri
              </label>
              <div className="muh-grid-2">
                <div className="form-group">
                  <label className="form-label">Ad</label>
                  <input className="form-input" value={formInfo.ad} onChange={e => handleFormInfoChange('ad', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Soyad</label>
                  <input className="form-input" value={formInfo.soyad} onChange={e => handleFormInfoChange('soyad', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Okul</label>
                  <input className="form-input" value={formInfo.okul_adi} onChange={e => handleFormInfoChange('okul_adi', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Sınıf</label>
                  <input className="form-input" value={formInfo.sinif_adi} onChange={e => handleFormInfoChange('sinif_adi', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Sınav Türü</label>
                  <input className="form-input" value={formInfo.sinav_turu} onChange={e => handleFormInfoChange('sinav_turu', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Alan</label>
                  <input className="form-input" value={formInfo.alan} onChange={e => handleFormInfoChange('alan', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Anne Adı</label>
                  <input className="form-input" value={formInfo.anne_adi} onChange={e => handleFormInfoChange('anne_adi', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Anne Telefon</label>
                  <input className="form-input" value={formInfo.anne_telefon} onChange={e => handleFormInfoChange('anne_telefon', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Baba Adı</label>
                  <input className="form-input" value={formInfo.baba_adi} onChange={e => handleFormInfoChange('baba_adi', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Baba Telefon</label>
                  <input className="form-input" value={formInfo.baba_telefon} onChange={e => handleFormInfoChange('baba_telefon', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Telefon</label>
                  <input className="form-input" value={formInfo.telefon} onChange={e => handleFormInfoChange('telefon', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">T.C. Kimlik</label>
                  <input className="form-input" value={formInfo.tc_kimlik} onChange={e => handleFormInfoChange('tc_kimlik', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Doğum Tarihi</label>
                  <input className="form-input" type="date" value={formInfo.dogum_tarihi} onChange={e => handleFormInfoChange('dogum_tarihi', e.target.value)} />
                </div>
                <label style={{ display:'flex', alignItems:'center', gap:8, fontWeight:700 }}>
                  <input type="checkbox" checked={formInfo.sehit_gazi_cocugu} onChange={e => handleFormInfoChange('sehit_gazi_cocugu', e.target.checked)} />
                  Şehit/Gazi Çocuğu
                </label>
                <label style={{ display:'flex', alignItems:'center', gap:8, fontWeight:700 }}>
                  <input type="checkbox" checked={formInfo.sosyal_medya_izni} onChange={e => handleFormInfoChange('sosyal_medya_izni', e.target.checked)} />
                  Sosyal Medya İzni
                </label>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Adres</label>
                  <textarea className="form-input" rows={2} value={formInfo.adres} onChange={e => handleFormInfoChange('adres', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          <div className="form-group" style={{ gridColumn:'1/-1', background:'rgba(199,46,69,0.05)', padding:15, borderRadius:12, border:'1px solid rgba(199,46,69,0.25)' }}>
            <label className="form-label" style={{ fontWeight:800, marginBottom:10, display:'block' }}>
              Sözleşme ve Senet Bilgileri
            </label>
            <div className="muh-grid-2">
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Kurumun Resmî Unvanı *</label>
                <input className="form-input" value="YED KURS" readOnly />
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Kurum Adresi</label>
                <textarea className="form-input" rows={2} value={form.belge_bilgileri.kurum_adresi} onChange={e => handleDocumentInfoChange('kurum_adresi', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Kurum Telefonu</label>
                <input className="form-input" value={form.belge_bilgileri.kurum_telefonu} onChange={e => handleDocumentInfoChange('kurum_telefonu', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Vergi / Kurum No</label>
                <input className="form-input" value={form.belge_bilgileri.kurum_vergi_no} onChange={e => handleDocumentInfoChange('kurum_vergi_no', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Senet Borçlusu (Veli) *</label>
                <input className="form-input" value={form.belge_bilgileri.borclu_ad_soyad} onChange={e => handleDocumentInfoChange('borclu_ad_soyad', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Veli T.C. Kimlik No</label>
                <input className="form-input" maxLength={11} value={form.belge_bilgileri.borclu_tc_kimlik} onChange={e => handleDocumentInfoChange('borclu_tc_kimlik', e.target.value.replace(/\D/g, '').slice(0, 11))} />
              </div>
              <div className="form-group">
                <label className="form-label">Veli Telefonu</label>
                <input className="form-input" value={form.belge_bilgileri.borclu_telefon} onChange={e => handleDocumentInfoChange('borclu_telefon', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Öğrencinin Doğum Yeri</label>
                <input className="form-input" value={form.belge_bilgileri.dogum_yeri} onChange={e => handleDocumentInfoChange('dogum_yeri', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Şube</label>
                <select className="form-input" value={form.belge_bilgileri.sube} onChange={e => handleDocumentInfoChange('sube', e.target.value)}>
                  <option value="">— Seçin —</option>
                  <option value="AVM">AVM</option>
                  <option value="Bulvar">Bulvar</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Düzenleme Yeri</label>
                <input className="form-input" value={form.belge_bilgileri.duzenleme_yeri} onChange={e => handleDocumentInfoChange('duzenleme_yeri', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Ödeme Yeri</label>
                <input className="form-input" value={form.belge_bilgileri.odeme_yeri} onChange={e => handleDocumentInfoChange('odeme_yeri', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Kitap Ücreti</label>
                <input className="form-input" type="number" min="0" value={form.belge_bilgileri.kitap_ucreti} onChange={e => handleDocumentInfoChange('kitap_ucreti', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Yayın Ücreti</label>
                <input className="form-input" type="number" min="0" value={form.belge_bilgileri.yayin_ucreti} onChange={e => handleDocumentInfoChange('yayin_ucreti', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Peşinat</label>
                <input className="form-input" type="number" min="0" value={form.belge_bilgileri.pesinat} onChange={e => handleDocumentInfoChange('pesinat', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Eğitim Bursu Yüzdesi</label>
                <input className="form-input" type="number" min="0" max="100" value={form.belge_bilgileri.burs_yuzdesi} onChange={e => handleDocumentInfoChange('burs_yuzdesi', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">KDV Oranı / Açıklaması</label>
                <input className="form-input" placeholder="Örn. %10" value={form.belge_bilgileri.kdv_orani} onChange={e => handleDocumentInfoChange('kdv_orani', e.target.value)} />
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Veli / Borçlu Adresi</label>
                <textarea className="form-input" rows={2} value={form.belge_bilgileri.borclu_adres} onChange={e => handleDocumentInfoChange('borclu_adres', e.target.value)} />
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Veli İş Adresi</label>
                <textarea className="form-input" rows={2} value={form.belge_bilgileri.borclu_is_adresi} onChange={e => handleDocumentInfoChange('borclu_is_adresi', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Kefil Adı Soyadı (Varsa)</label>
                <input className="form-input" value={form.belge_bilgileri.kefil_ad_soyad} onChange={e => handleDocumentInfoChange('kefil_ad_soyad', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Kefil T.C. Kimlik No</label>
                <input className="form-input" maxLength={11} value={form.belge_bilgileri.kefil_tc_kimlik} onChange={e => handleDocumentInfoChange('kefil_tc_kimlik', e.target.value.replace(/\D/g, '').slice(0, 11))} />
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Kefil / Avalist Adresi (Varsa)</label>
                <textarea className="form-input" rows={2} value={form.belge_bilgileri.kefil_adres} onChange={e => handleDocumentInfoChange('kefil_adres', e.target.value)} />
              </div>
            </div>
            <div style={{ marginTop:8, fontSize:'0.76rem', color:'var(--text-muted)', lineHeight:1.45 }}>
              Tüm otomatik veya manuel taksitler, tutarlarıyla birlikte tek sayfalık senet defteri çizelgesine aktarılır.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Eğitim Yılı</label>
            <input className="form-input" value={form.egitim_yili} onChange={e => handleChange('egitim_yili', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Paket</label>
            <select className="form-input" value={form.paket} onChange={e => handleChange('paket', e.target.value)}>
              {PAKETLER.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Başlangıç</label>
            <input className="form-input" type="date" value={form.baslangic_tarihi} onChange={e => handleChange('baslangic_tarihi', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Bitiş</label>
            <input className="form-input" type="date" value={form.bitis_tarihi} onChange={e => handleChange('bitis_tarihi', e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Brüt Ücret (₺) *</label>
            <input className="form-input" type="number" value={form.brut_ucret}
              onChange={e => handleChange('brut_ucret', e.target.value)} placeholder="0" />
          </div>

          <div className="form-group">
            <label className="form-label">İndirim Şablonu</label>
            <select className="form-input" value={form.sablon_id}
              onChange={e => handleSablonChange(e.target.value)}>
              <option value="">— Şablon seçin (opsiyonel) —</option>
              {sablonlar.map(s => (
                <option key={s.id} value={s.id}>
                  {s.ad} {parseFloat(s.yuzde) > 0 ? `(%${s.yuzde})` : parseFloat(s.sabit_tutar) > 0 ? `(${TL(s.sabit_tutar)})` : ''}
                </option>
              ))}
            </select>
          </div>

          {secilenSablon && (
            <div style={{ gridColumn:'1/-1', padding:'10px 14px', borderRadius:10,
              background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.3)',
              display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:'1.3rem' }}>✅</span>
              <div>
                <div style={{ fontWeight:700, fontSize:'0.875rem', color:'#065F46' }}>
                  {secilenSablon.ad} uygulandı
                </div>
                <div style={{ fontSize:'0.78rem', color:'#065F46' }}>
                  {parseFloat(secilenSablon.yuzde)>0
                    ? `%${secilenSablon.yuzde} indirim → ${TL((parseFloat(form.brut_ucret)||0)*parseFloat(secilenSablon.yuzde)/100)} indirim`
                    : `${TL(secilenSablon.sabit_tutar)} sabit indirim`}
                </div>
              </div>
              <button style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'#065F46', fontSize:'1rem' }}
                onClick={() => handleSablonChange('')}>✕</button>
            </div>
          )}

          {!form.sablon_id && (
            <>
              <div className="form-group">
                <label className="form-label">İndirim Tutarı (₺)</label>
                <input className="form-input" type="number" value={form.indirim_tutar}
                  onChange={e => handleChange('indirim_tutar', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">İndirim % (veya tutar kullan)</label>
                <input className="form-input" type="number" value={form.indirim_yuzde}
                  onChange={e => handleChange('indirim_yuzde', e.target.value)} />
              </div>
            </>
          )}

          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">Net Ücret (₺) *</label>
            <div style={{ position:'relative' }}>
              <input className="form-input" type="number" value={form.net_ucret}
                onChange={e => handleChange('net_ucret', e.target.value)}
                style={{ fontWeight:800, fontSize:'1.15rem', color:'var(--blue)', paddingRight:120 }} />
              {parseFloat(form.brut_ucret) > 0 && parseFloat(form.brut_ucret) !== parseFloat(form.net_ucret) && (
                <div style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)',
                  fontSize:'0.75rem', color:'#EF4444', fontWeight:700 }}>
                  -{TL(parseFloat(form.brut_ucret) - parseFloat(form.net_ucret))} indirim
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Ödeme Tipi</label>
            <select className="form-input" value={form.odeme_tipi} onChange={e => handleChange('odeme_tipi', e.target.value)}>
              <option value="pesin">Peşin</option>
              <option value="taksit">Taksit</option>
              <option value="kredi_karti">Kredi Kartı</option>
              <option value="senet">Senet</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Taksit Sayısı</label>
            <input className="form-input" type="number" min="1" max={MAX_TAKSIT_SAYISI} value={form.taksit_sayisi}
              onChange={e => handleChange('taksit_sayisi', e.target.value)}
              disabled={form.odeme_tipi === 'pesin' || form.taksit_modu === 'manuel'} />
          </div>

          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">Taksit Modu</label>
            <div style={{ display:'flex', gap:8 }}>
              <button type="button" className={`chip ${form.taksit_modu === 'otomatik' ? 'active' : ''}`} onClick={() => handleChange('taksit_modu', 'otomatik')}>
                Otomatik
              </button>
              <button type="button" className={`chip ${form.taksit_modu === 'manuel' ? 'active' : ''}`} onClick={() => handleChange('taksit_modu', 'manuel')}>
                Manuel
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">İlk Taksit Tarihi</label>
            <input className="form-input" type="date" value={form.ilk_odeme_tarihi}
              onChange={e => handleChange('ilk_odeme_tarihi', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Aylık Taksit Tutarı (~)</label>
            <div style={{ padding:'10px 14px', background:'var(--bg-elevated)', borderRadius:10,
              fontWeight:800, color:'#10B981', fontFamily:'var(--font-ui)' }}>
              {form.taksit_modu === 'manuel' ? TL(manualTotal) : TL((parseFloat(form.net_ucret)||0) / (parseInt(form.taksit_sayisi)||1))}
            </div>
          </div>

          {form.taksit_modu === 'manuel' && (
            <div className="form-group" style={{ gridColumn:'1/-1' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <label className="form-label" style={{ margin:0 }}>Manuel Taksitler</label>
                <button type="button" className="btn btn-outline btn-sm" onClick={addManualInstallment}>+ Satır Ekle</button>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8, overflowX:'auto', paddingBottom:4 }}>
                {form.taksitler.map((t, i) => (
                  <div key={i} style={{ display:'grid', gridTemplateColumns:'42px 145px 120px 120px 145px 145px 34px', gap:6, alignItems:'center', minWidth:760 }}>
                    <div style={{ fontWeight:800, textAlign:'center' }}>#{i + 1}</div>
                    <input className="form-input" type="date" value={t.vade_tarihi} onChange={e => handleInstallmentChange(i, 'vade_tarihi', e.target.value)} />
                    <input className="form-input" type="number" placeholder="Tutar" value={t.tutar} onChange={e => handleInstallmentChange(i, 'tutar', e.target.value)} />
                    <select className="form-input" value={t.durum} onChange={e => handleInstallmentChange(i, 'durum', e.target.value)}>
                      <option value="bekliyor">Bekliyor</option>
                      <option value="odendi">Ödendi</option>
                      <option value="gecikti">Gecikti</option>
                      <option value="iptal">İptal</option>
                    </select>
                    <input className="form-input" type="date" value={t.odeme_tarihi} onChange={e => handleInstallmentChange(i, 'odeme_tarihi', e.target.value)} />
                    <select className="form-input" value={t.odeme_yontemi} onChange={e => handleInstallmentChange(i, 'odeme_yontemi', e.target.value)}>
                      <option value="">Yöntem</option>
                      <option value="nakit">Nakit</option>
                      <option value="kredi_karti">Kredi Kartı</option>
                      <option value="banka_havalesi">Banka Havalesi</option>
                      <option value="pos">POS</option>
                    </select>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeManualInstallment(i)}>✕</button>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:8, fontSize:'0.82rem', fontWeight:800, color:Math.abs(manualDiff) > 0.01 ? '#EF4444' : '#10B981' }}>
                Toplam: {TL(manualTotal)} · Net ücret farkı: {TL(Math.abs(manualDiff))}
              </div>
            </div>
          )}

          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">Notlar</label>
            <textarea className="form-input" rows={2} value={form.notlar}
              onChange={e => handleChange('notlar', e.target.value)} />
          </div>
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:16 }}>
          <button className="btn btn-secondary" onClick={onClose}>İptal</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <><div className="spinner" style={{width:16,height:16}} /> Kaydediliyor...</> : '💾 Sözleşme Oluştur'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal: Tahsilat ──────────────────────────────────────────────
function TahsilatModal({ taksit, taksitler, onSave, onClose }) {
  const [form, setForm] = useState({
    tutar: taksitKalani(taksit) || '',
    odeme_yontemi: 'nakit',
    odeme_tarihi: localToday(),
    makbuz_no: makeReceiptNo(taksit),
    notlar: '',
  });
  const [saving, setSaving] = useState(false);
  const onizleme = tahsilatOnizleme(taksitler, taksit, form.tutar);
  const handleSubmit = async () => {
    if (Number(form.tutar) <= 0) return toast.error('0’dan büyük bir tahsilat tutarı giriniz');
    if (onizleme.fazla > 0) return toast.error(`En fazla ${TL(onizleme.toplamKalan)} tahsil edilebilir`);
    const baslangicTaksitId = onizleme.dagitimlar[0]?.id || taksit.id;
    setSaving(true);
    try { await onSave({ ...form, taksit_id: baslangicTaksitId }); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">💳 Tahsilat Al</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ background:'var(--bg-elevated)', borderRadius:12, padding:'12px 16px', marginBottom:16 }}>
          <div style={{ fontWeight:700 }}>{taksit.ad} {taksit.soyad} — Taksit #{taksit.taksit_no}</div>
          <div style={{ fontSize:'0.82rem', color:'var(--text-muted)' }}>
            Vade: {new Date(taksit.vade_tarihi).toLocaleDateString('tr-TR')} · Kalan: {TL(taksitKalani(taksit))}
          </div>
        </div>
        <div style={{ background:'#EFF6FF', color:'#1E40AF', border:'1px solid #BFDBFE', borderRadius:10, padding:10, marginBottom:14, fontSize:'0.78rem', fontWeight:650 }}>
          Önce seçtiğiniz taksitten önce kalan kısmi borç tamamlanır; ardından ödeme seçtiğiniz taksite işlenir.
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div className="form-group"><label className="form-label">Tahsil Tutarı (₺) *</label>
            <input className="form-input" type="number" min="0.01" step="0.01" value={form.tutar} onChange={e => setForm(f=>({...f,tutar:e.target.value}))} /></div>
          {onizleme.dagitimlar.length > 0 && <div style={{padding:10,borderRadius:10,background:onizleme.fazla>0?'#FEF2F2':'#ECFDF5',border:`1px solid ${onizleme.fazla>0?'#FCA5A5':'#A7F3D0'}`}}>
            <div style={{fontWeight:800,fontSize:'0.8rem'}}>Tahsilat dağılımı</div>
            {onizleme.dagitimlar.map(t => <div key={t.id} style={{display:'flex',justifyContent:'space-between',fontSize:'0.76rem',marginTop:5}}><span>#{t.taksit_no} taksit</span><strong>{TL(t.uygulanan)} → kalan {TL(t.yeniKalan)}</strong></div>)}
            {onizleme.fazla>0 && <div style={{color:'#B91C1C',fontWeight:800,fontSize:'0.76rem',marginTop:7}}>Fazla tutar: {TL(onizleme.fazla)}</div>}
          </div>}
          <div className="form-group"><label className="form-label">Ödeme Yöntemi</label>
            <select className="form-input" value={form.odeme_yontemi} onChange={e => setForm(f=>({...f,odeme_yontemi:e.target.value}))}>
              <option value="nakit">Nakit</option>
              <option value="kredi_karti">Kredi Kartı</option>
              <option value="banka_havalesi">Banka Havalesi</option>
              <option value="pos">POS</option>
            </select></div>
          <div className="form-group"><label className="form-label">Ödeme Tarihi</label>
            <input className="form-input" type="date" value={form.odeme_tarihi} onChange={e => setForm(f=>({...f,odeme_tarihi:e.target.value}))} /></div>
          <div className="form-group"><label className="form-label">Makbuz / Fiş No</label>
            <input className="form-input" value={form.makbuz_no} onChange={e => setForm(f=>({...f,makbuz_no:e.target.value}))} /></div>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:16 }}>
          <button className="btn btn-secondary" onClick={onClose}>İptal</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || onizleme.fazla > 0 || Number(form.tutar) <= 0}>
            {saving ? <><div className="spinner" style={{width:16,height:16}} /> Kaydediliyor...</> : '✅ Tahsilatı Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Ana Sayfa ────────────────────────────────────────────────────
export default function SozlesmelerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 900 : false);
  const [sozlesmeler, setSozlesmeler] = useState([]);
  const [sablonlar, setSablonlar]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [total, setTotal]             = useState(0);
  const [filtre, setFiltre]           = useState({ durum:'', sinif_id:'', arama:'', sayfa:1 });
  const [aramaMetni, setAramaMetni]   = useState('');
  const [siniflar, setSiniflar]       = useState([]);
  const [showModal, setShowModal]     = useState(false);
  const [secili, setSecili]           = useState(null);
  const [detay, setDetay]             = useState(null);
  const [tahsilatTaksit, setTahsilatTaksit] = useState(null);

  // Toplu işlem seçimi
  const [selectedIds, setSelectedIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ ...filtre, limit: SOZLESME_PAGE_SIZE });
      const [r, taksitResponse] = await Promise.all([
        axios.get(`/muhasebe/sozlesmeler?${params}`),
        axios.get('/muhasebe/taksitler').catch(() => null),
      ]);
      if (r.data.success) { 
        const selectedClass = filtre.sinif_id
          ? siniflar.find(sinif => String(sinif.id) === String(filtre.sinif_id))
          : null;
        let responseRows = r.data.data || [];
        let responseTotal = Number(r.data.total || 0);
        let clientSideClassFilter = false;

        // Eski/canlı API sınıf filtresini uygulamıyorsa başka sınıfları asla
        // göstermemek için tüm sözleşmeleri alıp seçilen sınıfı burada süzüyoruz.
        const serverAppliedClassFilter = !selectedClass || (
          responseRows.every(sozlesme => (
            sozlesme.sinif_id !== undefined
            && contractMatchesClass(sozlesme, selectedClass)
          ))
        );

        if (selectedClass && !serverAppliedClassFilter) {
          const fallbackParams = new URLSearchParams({
            ...filtre,
            sayfa: 1,
            limit: 2000,
          });
          const fallbackResponse = await axios.get(`/muhasebe/sozlesmeler?${fallbackParams}`);
          responseRows = extractList(fallbackResponse.data)
            .filter(sozlesme => contractMatchesClass(sozlesme, selectedClass));
          responseTotal = responseRows.length;
          clientSideClassFilter = true;
        }

        const responsePageCount = Math.max(1, Math.ceil(responseTotal / SOZLESME_PAGE_SIZE));
        const tumTaksitler = extractList(taksitResponse?.data);
        const gercekOzetler = summarizePaymentsByContract(tumTaksitler);
        const visibleRows = clientSideClassFilter
          ? responseRows.slice(
              (Number(filtre.sayfa) - 1) * SOZLESME_PAGE_SIZE,
              Number(filtre.sayfa) * SOZLESME_PAGE_SIZE
            )
          : responseRows;
        setSozlesmeler(visibleRows.map(sozlesme => ({
          ...sozlesme,
          ...(gercekOzetler.get(String(sozlesme.id)) || {}),
        })));
        setTotal(responseTotal);
        setSelectedIds([]); // Filtre değişince seçimleri temizle
        if (Number(filtre.sayfa) > responsePageCount) {
          setFiltre(current => ({ ...current, sayfa: responsePageCount }));
        }
      }
    } catch {} finally { setLoading(false); }
  }, [filtre, siniflar]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const arama = aramaMetni.trim().replace(/\s+/g, ' ');
      setFiltre(current => current.arama === arama
        ? current
        : { ...current, arama, sayfa: 1 });
    }, 300);

    return () => clearTimeout(timer);
  }, [aramaMetni]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    axios.get('/muhasebe/indirim-sablonlari').then(r => { if (r.data.success) setSablonlar(r.data.data || []); }).catch(()=>{});
    axios.get('/siniflar').then(r => setSiniflar(extractList(r.data))).catch(() => setSiniflar([]));
  }, []);

  const handleDetay = async (id) => {
    setSecili(id);
    try {
      const [r, taksitResponse, odemeResponse] = await Promise.all([
        axios.get(`/muhasebe/sozlesmeler/${id}`),
        axios.get(`/muhasebe/taksitler?sozlesme_id=${id}`).catch(() => null),
        axios.get(`/muhasebe/odemeler?sozlesme_id=${id}&limit=10000`).catch(() => null),
      ]);
      if (r.data.success) {
        const gercekTaksitler = reconcileInstallmentPayments(extractList(taksitResponse?.data));
        const kullanilanTaksitler = gercekTaksitler.length ? gercekTaksitler : (r.data.data.taksitler || []);
        const gercekOdemeler = extractList(odemeResponse?.data)
          .filter(odeme => String(odeme.sozlesme_id) === String(id));
        const ozet = summarizePaymentsByContract(kullanilanTaksitler).get(String(id));
        setDetay({
          ...r.data.data,
          taksitler: kullanilanTaksitler,
          odemeler: gercekOdemeler,
          ...(ozet || {}),
        });
        if (ozet) {
          setSozlesmeler(mevcut => mevcut.map(sozlesme => String(sozlesme.id) === String(id)
            ? { ...sozlesme, ...ozet }
            : sozlesme));
        }
      }
    } catch {}
  };

  const handleSave = async (form) => {
    try {
      const response = await axios.post('/muhasebe/sozlesmeler', form);
      const savedId = response.data?.data?.id;
      const savedForm = {
        ...form,
        id: savedId || form.id,
        belge_bilgileri: {
          ...form.belge_bilgileri,
          sozlesme_no: form.belge_bilgileri?.sozlesme_no || savedId || '',
        },
      };
      toast.success('Sözleşme oluşturuldu!');
      printCourseContract({
        form: savedForm,
        student: savedForm.sozlesme_form_bilgileri,
        representativeName: [user?.ad, user?.soyad].filter(Boolean).join(' ')
      });
      setShowModal(false); load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Hata');
    }
  };

  // ── TEKLİ VE TOPLU İŞLEM FONKSİYONLARI ──────────────────────────
  
  const handleDelete = async (id) => {
    if(!window.confirm("Bu sözleşmeyi tamamen silmek istediğinize emin misiniz? Bu işlem geri alınamaz.")) return;
    try {
      await axios.delete(`/muhasebe/sozlesmeler/${id}`);
      toast.success('Sözleşme silindi');
      if(secili === id) setSecili(null);
      load();
    } catch (e) { toast.error('Silme işlemi başarısız'); }
  };

  const handleStatusUpdate = async (id, newDurum) => {
    try {
      await axios.put(`/muhasebe/sozlesmeler/${id}`, { durum: newDurum });
      toast.success(`Durum güncellendi: ${newDurum}`);
      if (secili === id) handleDetay(id);
      load();
    } catch (e) { toast.error('Güncelleme başarısız'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === sozlesmeler.length) setSelectedIds([]);
    else setSelectedIds(sozlesmeler.map(s => s.id));
  };

  const handleBulkDelete = async () => {
    if(!window.confirm(`${selectedIds.length} adet sözleşmeyi SİLMEK istediğinize emin misiniz?`)) return;
    try {
      await axios.post('/muhasebe/sozlesmeler/bulk-delete', { ids: selectedIds });
      toast.success('Seçili kayıtlar silindi');
      load();
    } catch { toast.error('Toplu silme başarısız'); }
  };

  const handleBulkStatus = async (newDurum) => {
    if(!window.confirm(`${selectedIds.length} adet sözleşmeyi '${newDurum}' durumuna getirmek istiyor musunuz?`)) return;
    try {
      await axios.post('/muhasebe/sozlesmeler/bulk-status', { ids: selectedIds, durum: newDurum });
      toast.success('Seçili kayıtlar güncellendi');
      load();
    } catch { toast.error('Toplu güncelleme başarısız'); }
  };

  const handleTahsilat = async (form) => {
    try {
      await axios.post('/muhasebe/tahsilat', form);
      toast.success('Tahsilat kaydedildi!');
      
      if (detay) {
        const studentInfo = detay?.sozlesme_form_bilgileri || {
          ad: detay?.ad,
          soyad: detay?.soyad,
          veli_adi: detay?.veli_adi,
          veli_telefon: detay?.veli_tel,
          sinif_adi: detay?.sinif_adi,
          paket: detay?.paket,
          egitim_yili: detay?.egitim_yili,
        };
        printPaymentReceiptDocument({
          contract: detay,
          student: studentInfo,
          payment: form,
          installments: detay?.taksitler || [],
          payments: [...(detay?.odemeler || []), form],
          cashierName: [user?.ad, user?.soyad].filter(Boolean).join(' '),
          logoUrl: yedkursLogo,
        });
      }

      setTahsilatTaksit(null);
      if (secili) handleDetay(secili);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Hata'); }
  };

  const handleMakbuzYazdirOdeme = (payment) => {
    if (!detay) return;
    const studentInfo = detay?.sozlesme_form_bilgileri || {
      ad: detay?.ad,
      soyad: detay?.soyad,
      veli_adi: detay?.veli_adi,
      veli_telefon: detay?.veli_tel,
      sinif_adi: detay?.sinif_adi,
      paket: detay?.paket,
      egitim_yili: detay?.egitim_yili,
    };
    const cashierName = payment.kaydeden_ad
      ? `${payment.kaydeden_ad} ${payment.kaydeden_soyad || ''}`.trim()
      : [user?.ad, user?.soyad].filter(Boolean).join(' ');

    const opened = printPaymentReceiptDocument({
      contract: detay,
      student: studentInfo,
      payment,
      installments: detay.taksitler || [],
      payments: detay.odemeler || [],
      cashierName,
      logoUrl: yedkursLogo,
    });
    if (!opened) toast.error('Makbuz penceresi açılamadı. Tarayıcı açılır pencere (popup) iznini kontrol edin.');
  };

  const handleMakbuzYazdirTaksit = (taksit) => {
    if (!detay) return;
    const eslesenOdeme = (detay.odemeler || []).find(o => String(o.taksit_id) === String(taksit.id));
    const payment = eslesenOdeme || {
      id: taksit.id,
      taksit_id: taksit.id,
      taksit_no: taksit.taksit_no,
      vade_tarihi: taksit.vade_tarihi,
      tutar: taksit.odeme_tutari || taksit.tutar,
      odeme_tarihi: taksit.gercek_odeme || taksit.odeme_tarihi || localToday(),
      odeme_yontemi: taksit.odeme_yontemi || 'nakit',
      makbuz_no: taksit.makbuz_no || makeReceiptNo(taksit),
      notlar: taksit.notlar || '',
    };
    handleMakbuzYazdirOdeme(payment);
  };

  const handleGenelMakbuzYazdir = () => {
    if (!detay) return;
    const studentInfo = detay?.sozlesme_form_bilgileri || {
      ad: detay?.ad,
      soyad: detay?.soyad,
      veli_adi: detay?.veli_adi,
      veli_telefon: detay?.veli_tel,
      sinif_adi: detay?.sinif_adi,
      paket: detay?.paket,
      egitim_yili: detay?.egitim_yili,
    };
    const totalPaid = (detay.odemeler || []).reduce((sum, p) => sum + Number(p.tutar || 0), 0)
      || (detay.taksitler || []).reduce((sum, t) => sum + Number(t.odeme_tutari || 0), 0);

    if (totalPaid <= 0) {
      toast.error('Bu sözleşmeye ait henüz tahsilat kaydı bulunmuyor.');
      return;
    }

    const generalPayment = {
      id: 'genel-ekstre',
      tutar: totalPaid,
      odeme_tarihi: localToday(),
      odeme_yontemi: 'karma',
      makbuz_no: `EKSTRE-${detay.id}-${localToday().replace(/-/g, '')}`,
      notlar: 'Toplam Tahsilat ve Hesap Dökümü Ekstresi',
      isGeneralStatement: true,
    };

    const opened = printPaymentReceiptDocument({
      contract: detay,
      student: studentInfo,
      payment: generalPayment,
      installments: detay.taksitler || [],
      payments: detay.odemeler || [],
      cashierName: [user?.ad, user?.soyad].filter(Boolean).join(' '),
      logoUrl: yedkursLogo,
    });
    if (!opened) toast.error('Makbuz penceresi açılamadı. Tarayıcı açılır pencere (popup) iznini kontrol edin.');
  };

  const detayTahsilatGruplari = React.useMemo(() => {
    if (!detay?.odemeler || !detay.odemeler.length) return [];
    const grouped = new Map();
    detay.odemeler.forEach(payment => {
      const key = payment.tahsilat_grup_uuid || `odeme-${payment.id}`;
      const current = grouped.get(key) || {
        ...payment,
        tutar: 0,
        taksitNolari: [],
        items: [],
      };
      current.tutar += Number(payment.tutar || 0);
      if (payment.taksit_no) {
        current.taksitNolari.push(Number(payment.taksit_no));
      }
      current.items.push(payment);
      grouped.set(key, current);
    });

    return [...grouped.values()].map(payment => {
      const taksitNolari = [...new Set(payment.taksitNolari)].sort((a, b) => a - b);
      return {
        ...payment,
        taksitNolari,
        taksitLabel: taksitNolari.length ? taksitNolari.map(n => `#${n}`).join(', ') : (payment.taksit_no ? `#${payment.taksit_no}` : '-'),
      };
    });
  }, [detay?.odemeler]);

  const DURUM_RENK = { aktif:'#10B981', iptal:'#EF4444', tamamlandi:'#3B82F6', donduruldu:'#F59E0B' };
  const DURUM_ETIKET = { aktif:'Aktif', iptal:'İptal', tamamlandi:'Tamamlandı', donduruldu:'Donduruldu' };
  const TAKSIT_RENK = { odendi:'#10B981', kismi:'#F59E0B', bekliyor:'#3B82F6', gecikti:'#EF4444', iptal:'#94A3B8' };
  const aktifSayfa = Number(filtre.sayfa) || 1;
  const toplamSayfa = Math.max(1, Math.ceil(total / SOZLESME_PAGE_SIZE));
  const aktifFiltreVar = Boolean(filtre.durum || filtre.sinif_id || filtre.arama);
  const filtreleriTemizle = () => {
    setAramaMetni('');
    setSecili(null);
    setDetay(null);
    setFiltre({ durum:'', sinif_id:'', arama:'', sayfa:1 });
  };
  const sayfaDegistir = (yeniSayfa) => {
    const guvenliSayfa = Math.min(toplamSayfa, Math.max(1, yeniSayfa));
    setSecili(null);
    setDetay(null);
    setFiltre(f => ({ ...f, sayfa: guvenliSayfa }));
  };
  const detayFormBilgileri = detay?.sozlesme_form_bilgileri || {};
  const detayOgrenciAdi = getStudentName(detayFormBilgileri) !== '-'
    ? getStudentName(detayFormBilgileri)
    : `${detay?.ad || ''} ${detay?.soyad || ''}`.trim();
  const detayYazdirmaFormu = detay ? {
    ...detay,
    taksit_modu: 'manuel',
    taksitler: detay.taksitler || [],
    sozlesme_form_bilgileri: detayFormBilgileri,
    belge_bilgileri: normalizeContractDocumentInfo(
      detay.belge_bilgileri || detay.belge_bilgileri_json,
      detayFormBilgileri,
      detay
    ),
  } : null;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      <div className="section-header animate-fadeInUp">
        <div>
          <h2 style={{ fontFamily:'var(--font-ui)', fontWeight:900 }}>📋 Sözleşmeler</h2>
          <div className="section-subtitle">{total} {aktifFiltreVar ? 'eşleşen' : 'toplam'} sözleşme</div>
        </div>
        
        <div className="muh-filter-row">
          {selectedIds.length > 0 && (
            <div className="bulk-actions-bar animate-fadeIn" style={{ display:'flex', gap:8, alignItems:'center', background:'var(--blue-dim)', padding:'4px 12px', borderRadius:12 }}>
              <span style={{ fontWeight:700, fontSize:'0.85rem', color:'var(--blue)' }}>{selectedIds.length} Seçili</span>
              <button className="btn btn-sm btn-outline" onClick={() => handleBulkStatus('aktif')}>Aktif</button>
              <button className="btn btn-sm btn-outline" onClick={() => handleBulkStatus('donduruldu')}>Dondur</button>
              <button className="btn btn-sm btn-outline" style={{ color:'#EF4444' }} onClick={handleBulkDelete}>Sil</button>
            </div>
          )}
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Yeni Sözleşme</button>
        </div>
      </div>

      <div className="card" style={{ padding:'16px' }}>
        <div className="muh-filter-row" style={{ alignItems:'flex-end' }}>
          <div className="form-group" style={{ flex:'1 1 320px', margin:0 }}>
            <label className="form-label" htmlFor="sozlesme-ogrenci-arama">Öğrenci ara</label>
            <div style={{ position:'relative' }}>
              <input
                id="sozlesme-ogrenci-arama"
                className="form-input"
                type="search"
                autoComplete="off"
                placeholder="Ad veya soyad yazın..."
                value={aramaMetni}
                onChange={event => setAramaMetni(event.target.value)}
                style={{ paddingRight: aramaMetni ? 42 : undefined }}
              />
              {aramaMetni && (
                <button
                  type="button"
                  aria-label="Öğrenci aramasını temizle"
                  title="Aramayı temizle"
                  onClick={() => setAramaMetni('')}
                  style={{
                    position:'absolute', right:8, top:'50%', transform:'translateY(-50%)',
                    width:28, height:28, border:0, borderRadius:8, cursor:'pointer',
                    background:'transparent', color:'var(--text-muted)', fontSize:'1rem'
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          <div className="form-group" style={{ flex:'0 1 260px', minWidth:220, margin:0 }}>
            <label className="form-label" htmlFor="sozlesme-sinif-filtre">Sınıf</label>
            <select
              id="sozlesme-sinif-filtre"
              className="form-input"
              value={filtre.sinif_id}
              onChange={event => {
                setSecili(null);
                setDetay(null);
                setFiltre(current => ({ ...current, sinif_id:event.target.value, sayfa:1 }));
              }}
            >
              <option value="">Tüm sınıflar</option>
              {siniflar.map(sinif => (
                <option key={sinif.id} value={sinif.id}>
                  {sinif.ad || sinif.sinif_adi || `Sınıf #${sinif.id}`}
                </option>
              ))}
            </select>
          </div>
          {aktifFiltreVar && (
            <button className="btn btn-ghost" type="button" onClick={filtreleriTemizle}>
              Filtreleri temizle
            </button>
          )}
        </div>

        <div style={{ display:'flex', gap:8, overflowX:'auto', flexWrap:'nowrap', paddingTop:14, marginTop:14, borderTop:'1px solid var(--border)' }}>
          {['','aktif','tamamlandi','iptal','donduruldu'].map(d => (
            <button key={d} className={`chip ${filtre.durum===d?'active':''}`}
              style={{ flex:'0 0 auto' }}
              onClick={() => setFiltre(f => ({...f, durum:d, sayfa:1}))}>
              {d ? (DURUM_ETIKET[d] || d) : 'Tümü'}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:16 }}>
        <div className="card animate-fadeInUp" style={{ padding:0 }}>
          {loading ? (
            <div className="page-loader" style={{ minHeight:200 }}><div className="spinner" /></div>
          ) : isMobile ? (
            <div style={{ padding: 12, display:'flex', flexDirection:'column', gap:10 }}>
              {sozlesmeler.length === 0 ? (
                <div className="empty-state" style={{ padding: 36 }}>
                  <h3>{aktifFiltreVar ? 'Aramanıza uygun sözleşme bulunamadı' : 'Kayıt bulunamadı'}</h3>
                </div>
              ) : sozlesmeler.map(s => (
                <div
                  key={s.id}
                  className="card"
                  onClick={() => navigate(`/muhasebe/sozlesmeler/${s.id}`)}
                  style={{
                    cursor:'pointer',
                    padding: 12,
                    borderLeft: `4px solid ${DURUM_RENK[s.durum] || '#94A3B8'}`,
                    background:'var(--bg-card)',
                  }}
                >
                  <div style={{ display:'flex', justifyContent:'space-between', gap:10, alignItems:'flex-start' }}>
                    <label onClick={(e) => e.stopPropagation()} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                      <input type="checkbox" checked={selectedIds.includes(s.id)} onChange={() => toggleSelect(s.id)} />
                    </label>
                    <span className="badge" style={{ background:(DURUM_RENK[s.durum]||'#94A3B8')+'22', color:DURUM_RENK[s.durum]||'#94A3B8' }}>
                      {DURUM_ETIKET[s.durum] || s.durum}
                    </span>
                  </div>
                  <div style={{ fontWeight:800, marginTop: 6 }}>{s.ad} {s.soyad}</div>
                  <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:2 }}>{s.sinif_adi} · {s.egitim_yili}</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:10 }}>
                    <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>Net</div>
                    <div style={{ fontWeight:800, color:'var(--blue)', textAlign:'right' }}>{TL(s.net_ucret)}</div>
                    <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>Ödenen</div>
                    <div style={{ fontWeight:700, color:'#10B981', textAlign:'right' }}>{TL(s.odenen_tutar)}</div>
                    <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>Kalan</div>
                    <div style={{ fontWeight:700, color:s.kalan_tutar > 0 ? '#EF4444' : '#10B981', textAlign:'right' }}>{TL(s.kalan_tutar)}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}><input type="checkbox" checked={selectedIds.length === sozlesmeler.length && sozlesmeler.length > 0} onChange={toggleSelectAll} /></th>
                    <th>Öğrenci</th><th>Paket</th><th>İndirim</th><th>Net Ücret</th><th>Ödenen</th><th>Kalan</th><th>Durum</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {sozlesmeler.length === 0 ? (
                    <tr>
                      <td colSpan="9">
                        <div className="empty-state" style={{ padding:36 }}>
                          <h3>{aktifFiltreVar ? 'Aramanıza uygun sözleşme bulunamadı' : 'Kayıt bulunamadı'}</h3>
                        </div>
                      </td>
                    </tr>
                  ) : sozlesmeler.map(s => (
                    <tr key={s.id} style={{ cursor:'pointer', background: secili === s.id ? 'var(--blue-dim, #eff6ff)' : undefined }}
                      onClick={() => handleDetay(s.id)}>
                      <td onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.includes(s.id)} onChange={() => toggleSelect(s.id)} />
                      </td>
                      <td>
                        <div style={{ fontWeight:700 }}>{s.ad} {s.soyad}</div>
                        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{s.sinif_adi} · {s.egitim_yili}</div>
                      </td>
                      <td style={{ fontSize:'0.82rem' }}>{s.paket}</td>
                      <td>
                        <span style={{ fontSize:'0.78rem', color:'#10B981', fontWeight:700 }}>
                          {parseFloat(s.indirim_tutar) > 0 ? `-${TL(s.indirim_tutar)}` : '—'}
                        </span>
                      </td>
                      <td style={{ fontFamily:'var(--font-ui)', fontWeight:800, color:'var(--blue)' }}>{TL(s.net_ucret)}</td>
                      <td style={{ color:'#10B981', fontWeight:700 }}>{TL(s.odenen_tutar)}</td>
                      <td style={{ color: s.kalan_tutar > 0 ? '#EF4444' : '#10B981', fontWeight:700 }}>{TL(s.kalan_tutar)}</td>
                      <td>
                        <span className="badge" style={{ background:(DURUM_RENK[s.durum]||'#94A3B8')+'22', color:DURUM_RENK[s.durum]||'#94A3B8' }}>
                          {DURUM_ETIKET[s.durum] || s.durum}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/muhasebe/sozlesmeler/${s.id}`);
                          }}
                        >
                          Detay
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && (
            <div style={{
              padding:'14px 16px',
              borderTop:'1px solid var(--border)',
              display:'flex',
              alignItems:'center',
              justifyContent:'space-between',
              gap:12,
              flexWrap:'wrap',
            }}>
              <div style={{ color:'var(--text-muted)', fontSize:'0.9rem' }}>
                Sayfa <strong>{aktifSayfa}</strong> / <strong>{toplamSayfa}</strong> — Toplam <strong>{total}</strong> sözleşme
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={aktifSayfa <= 1}
                  onClick={() => sayfaDegistir(aktifSayfa - 1)}
                >
                  ← Önceki
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={aktifSayfa >= toplamSayfa}
                  onClick={() => sayfaDegistir(aktifSayfa + 1)}
                >
                  Sonraki →
                </button>
              </div>
            </div>
          )}
        </div>

        {secili && detay && (
          <div className="card animate-fadeInUp" style={{ padding:0 }}>
            <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <div style={{ fontFamily:'var(--font-ui)', fontWeight:800 }}>{detayOgrenciAdi}</div>
                <div style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>
                  {detay.paket} · {detay.egitim_yili} · {detayFormBilgileri.sinif_adi || detay.sinif_adi || '-'}
                </div>
                
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                   <select 
                    className="form-input" 
                    style={{ height: 28, fontSize: '0.75rem', width: 'auto', padding: '0 8px' }}
                    value={detay.durum}
                    onChange={(e) => handleStatusUpdate(detay.id, e.target.value)}
                   >
                     <option value="aktif">Aktif Yap</option>
                     <option value="donduruldu">Dondur</option>
                     <option value="iptal">İptal Et</option>
                     <option value="tamamlandi">Tamamlandı</option>
                   </select>
                   <button
                    className="btn btn-sm"
                    style={{ background: '#3B82F6', color: 'white', border: 'none', height: 28 }}
                    onClick={() => printCourseContract({
                      form: detayYazdirmaFormu,
                      student: detayFormBilgileri,
                      representativeName: [user?.ad, user?.soyad].filter(Boolean).join(' ')
                    })}
                   >
                     Sözleşme + Senetler
                   </button>
                   <button
                    className="btn btn-sm"
                    style={{ background: '#B7791F', color: 'white', border: 'none', height: 28 }}
                    onClick={() => printPromissoryNotes({
                      form: detayYazdirmaFormu,
                      student: detayFormBilgileri,
                    })}
                   >
                     Senet Defteri
                   </button>
                   <button 
                    className="btn btn-sm" 
                    style={{ background: '#EF4444', color: 'white', border: 'none', height: 28 }}
                    onClick={() => handleDelete(detay.id)}
                   >
                     Sil
                   </button>
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setSecili(null)}>✕</button>
            </div>

            <div className="muh-grid-4" style={{ padding:'12px 16px', gap:8, borderBottom:'1px solid var(--border)' }}>
              {[
                { l:'Brüt', v:TL(detay.brut_ucret), c:'var(--text-secondary)' },
                { l:'İndirim', v:TL(detay.indirim_tutar), c:'#10B981' },
                { l:'Net', v:TL(detay.net_ucret), c:'var(--blue)' },
                { l:'Kalan', v:TL(detay.taksitler?.reduce((s,t)=>s+taksitKalani(t),0)), c:'#EF4444' },
              ].map((i,k) => (
                <div key={k} style={{ textAlign:'center', padding:'10px', background:'var(--bg-elevated)', borderRadius:10 }}>
                  <div style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize: '0.85rem', color:i.c }}>{i.v}</div>
                  <div style={{ fontSize:'0.70rem', color:'var(--text-muted)' }}>{i.l}</div>
                </div>
              ))}
            </div>

            <div style={{ padding:'10px 16px', fontWeight:700, fontSize:'0.85rem', borderBottom:'1px solid var(--border)' }}>
              Taksit Planı
            </div>
            <div style={{ maxHeight:360, overflowY:'auto' }}>
              {(detay.taksitler || []).map((t, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 16px', borderBottom:'1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:'0.875rem' }}>#{t.taksit_no} — {TL(t.tutar)}</div>
                    {t.durum==='kismi' && <div style={{fontSize:'0.72rem',color:'#D97706',fontWeight:700}}>Ödenen {TL(t.odeme_tutari)} · Kalan {TL(taksitKalani(t))}</div>}
                    <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>
                      {new Date(t.vade_tarihi).toLocaleDateString('tr-TR')}
                      {t.gercek_odeme && ` · Ödendi: ${new Date(t.gercek_odeme).toLocaleDateString('tr-TR')}`}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <span className="badge" style={{ background:(TAKSIT_RENK[t.durum]||'#94A3B8')+'22', color:TAKSIT_RENK[t.durum]||'#94A3B8' }}>
                      {t.durum}
                    </span>
                    {(t.durum === 'odendi' || (t.durum === 'kismi' && Number(t.odeme_tutari) > 0)) && (
                      <button
                        className="btn btn-sm"
                        style={{ background: '#0284C7', color: 'white', border: 'none', fontWeight: 700 }}
                        onClick={() => handleMakbuzYazdirTaksit(t)}
                        title="Bu takside ait makbuzu çıkart"
                      >
                        🧾 Makbuz
                      </button>
                    )}
                    {(t.durum==='bekliyor'||t.durum==='gecikti'||t.durum==='kismi') && (
                      <button className="btn btn-sm" style={{ background:'#10B981', color:'white', border:'none' }}
                        onClick={() => setTahsilatTaksit({...t, ad:detayFormBilgileri.ad || detay.ad, soyad:detayFormBilgileri.soyad || detay.soyad})}>
                        💳 Al
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              padding: '10px 16px',
              fontWeight: 700,
              fontSize: '0.85rem',
              borderTop: '2px solid var(--border)',
              borderBottom: '1px solid var(--border)',
              background: 'var(--bg-elevated)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>🧾 Tahsilat Geçmişi ({detayTahsilatGruplari.length})</div>
              {detayTahsilatGruplari.length > 0 && (
                <button
                  className="btn btn-sm btn-outline"
                  style={{ fontSize: '0.75rem', height: 26, padding: '0 8px', color: '#0284C7', borderColor: '#38BDF8' }}
                  onClick={handleGenelMakbuzYazdir}
                  title="Tüm tahsilatların toplu hesap dökümü ve makbuzu"
                >
                  🖨️ Toplu Makbuz / Ekstre
                </button>
              )}
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {detayTahsilatGruplari.length === 0 ? (
                <div style={{ padding: '14px 16px', color: 'var(--text-muted)', fontSize: '0.8rem', textAlign: 'center' }}>
                  Henüz kaydedilmiş tahsilat bulunmuyor.
                </div>
              ) : (
                detayTahsilatGruplari.map((payment, i) => (
                  <div
                    key={payment.id || i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 16px',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '0.875rem', color: '#059669' }}>
                        {TL(payment.tutar)} · <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>Taksit {payment.taksitLabel}</span>
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                        {new Date(payment.odeme_tarihi).toLocaleDateString('tr-TR')} · {paymentMethodLabel(payment.odeme_yontemi)}
                        {payment.makbuz_no && ` · No: ${payment.makbuz_no}`}
                      </div>
                      {payment.notlar && (
                        <div style={{ fontSize: '0.70rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 1 }}>
                          {payment.notlar}
                        </div>
                      )}
                    </div>
                    <div>
                      <button
                        className="btn btn-sm"
                        style={{
                          background: '#0284C7',
                          color: 'white',
                          border: 'none',
                          fontWeight: 700,
                          fontSize: '0.76rem',
                          padding: '5px 10px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                        onClick={() => handleMakbuzYazdirOdeme(payment)}
                        title="Bu ödemenin makbuzunu çıkart"
                      >
                        🖨️ Makbuz Çıkart
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {showModal && <SozlesmeModal sablonlar={sablonlar} onSave={handleSave} onClose={() => setShowModal(false)} />}
      {tahsilatTaksit && <TahsilatModal taksit={tahsilatTaksit} taksitler={detay?.taksitler || [tahsilatTaksit]} onSave={handleTahsilat} onClose={() => setTahsilatTaksit(null)} />}
    </div>
  );
}
